import { t } from '@lingui/core/macro';
import { IPublicSession, updatePublicSession, userSession } from 'state/session';
import { showToast } from 'state/toast';
import { myCameraStream } from 'state/video';
import { user } from 'utils/auth/user';
import logger from 'utils/log';
import { sendApiRequest } from 'utils/network';
import { RTCClient } from 'webrtc';
import { EStreamSessionEvents, EWebsocketClientEvents } from 'webrtc/events';
import { SignalerClient } from 'webrtc/signal';
import type { TRTCConnection } from 'webrtc/types';
import { Unsubscribe } from 'utils/events';
import { isOwnDealerPeer } from 'utils/ui/voipRemotes';
import { State } from 'utils/state';
import { applyRemoteSession } from 'state/session/applyRemote';
import { getDirtyKeys } from 'state/session/dirtyKeys';
import { repairPublicSession } from 'state/session/readRepair';
import { isSyncDegraded, setSyncDegraded } from 'state/session/syncStatus';
import { isDebugMode } from './debug';
import { isInheritedSession } from 'state/session/inheritedSession';

export let broadcastingConnection: SignalerClient;
export let voipRtcConnection: RTCClient;

/**
 * AVS-4302: bumped every time `initWebRtc` (re)builds `voipRtcConnection`. React consumers
 * (`useVoipConnections`) subscribe to it so a rebuild forces them to re-render and re-subscribe
 * to the NEW `RTCClient.store`. Without this, a takeover / step-down rebuilds the connection
 * while the VoipPanel stays mounted (View.tsx keeps `voipStream` true), so the hook keeps its
 * subscription to the OLD (destroyed) store and never sees the new master's loopback — the
 * stepped-down master is stuck on a placeholder. `voipRtcConnection` is a module binding whose
 * reassignment is invisible to React; this State makes the rebuild observable.
 */
export const voipConnectionEpoch = new State<{ value: number }>({ value: 0 });
export let broadcastingConnectionId: string;

// Handler-level reentrancy guard for this SessionUpdated listener; also drives the
// updatePublicSession.cancel() that discards the pending outbound debounced sync. Distinct from
// applyRemoteSession's internal runApplyingRemote guard (which stops syncToServer dirty-marking the
// inbound change and self-skipping the next broadcast) — different mechanisms, both required (AVS-4330).
let isApplyingBroadcastUpdate = false;

// AVS-4330 degraded-mode fallback. When the broadcasting socket gives up reconnecting, the client
// can no longer RECEIVE broadcasts, so it slow-polls the session record to self-heal missed
// (persisted) changes. Owned here — not in initWebRtc — because initSessionBroadcasting also runs
// for voip-disabled / customer sessions, which initWebRtc early-returns out of.
export const SYNC_DEGRADED_POLL_INTERVAL_MS = 30_000;
let syncPollInterval: ReturnType<typeof setInterval> | null = null;
let onlineListenerRegistered = false;

function stopSyncDegradedPoll() {
  if (syncPollInterval !== null) {
    clearInterval(syncPollInterval);
    syncPollInterval = null;
  }
  setSyncDegraded(false);
}

function startSyncDegradedPoll() {
  setSyncDegraded(true);
  if (syncPollInterval !== null) {
    return; // idempotent — a second give-up must not start a second timer
  }
  // Jitter so a fleet that all give up at once (a signaling outage) doesn't GET in lockstep.
  const interval = SYNC_DEGRADED_POLL_INTERVAL_MS + Math.floor(Math.random() * 10_000);
  syncPollInterval = setInterval(() => {
    // Self-terminate once the session is gone/ended — covers customers, who never emit
    // 'session:end', so the poll can't outlive the session.
    if (!userSession().get('id') || userSession().get('endedAt')) {
      stopSyncDegradedPoll();
      return;
    }
    void repairPublicSession();
  }, interval);
}

// Connectivity restored: heal immediately, and if we had given up entirely, rebuild the dead
// socket (initSessionBroadcasting makes a fresh SignalerClient, since the dead one fails isReady()).
function handleBroadcastingOnline() {
  void repairPublicSession();
  if (isSyncDegraded()) {
    initSessionBroadcasting();
  }
}

// AVS-4302: the rtc-level EventEmitter handlers registered in initWebRtc
// (`voip:connected`, `peer:disconnected`, `peer:connected` on the RTCClient, and
// `session:end` on the persistent userSession() singleton) are re-registered on every
// initWebRtc. Without removing the previous ones they stack across init→destroy→init
// cycles. `session:end` is the worst offender: it lives on the userSession() singleton,
// which survives RTCClient.instance nulling, so it accumulates across reconnect cycles.
// We collect each registration's unsubscribe here and clear them before re-registering.
let voipHandlerUnsubscribes: Unsubscribe[] = [];

function cleanupVoipHandlers() {
  voipHandlerUnsubscribes.forEach((unsubscribe) => {
    if (typeof unsubscribe === 'function') unsubscribe();
  });
  voipHandlerUnsubscribes = [];
}

async function getIceServers() {
  // if ICE_SERVERS_ORIGIN is not set, it will go to the default value which is fine for our deployed instances
  return sendApiRequest<RTCIceServer[]>('/ice-servers', { origin: process.env.ICE_SERVERS_ORIGIN });
}

export function initSessionBroadcasting() {
  // Sometimes session ID is not yet available. If so, exit early and retry when session ID becomes available
  if (!userSession().get('id')) {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let unsubscribed = false;

    const unsub = userSession().on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
      if (changedKeys.includes('id') && session.id && !unsubscribed) {
        if (timeoutId) clearTimeout(timeoutId);
        unsubscribed = true;
        unsub();
        initSessionBroadcasting();
      }
    });

    // Failsafe: If session ID is not set within 10 seconds, log error and unsubscribe
    timeoutId = setTimeout(() => {
      if (!unsubscribed) {
        unsubscribed = true;
        unsub();
        console.error('[Session Broadcast] Timeout waiting for session ID - broadcasting not initialized');
      }
    }, 10000);

    logger.log('[Session Broadcast] Waiting for session ID to initialize broadcasting');
    return;
  }

  // Register the online-recovery listener once per tab (initSessionBroadcasting is re-entrant).
  if (!onlineListenerRegistered && typeof window !== 'undefined') {
    window.addEventListener('online', handleBroadcastingOnline);
    onlineListenerRegistered = true;
  }

  const broadcastingApiUrl = `${process.env.SIGNALING_ORIGIN}?sessionId=${userSession().get('id')}&topicId=session`;

  if (broadcastingConnection && broadcastingConnection.isReady()) {
    if (broadcastingConnection.wsApiUrl !== broadcastingApiUrl) {
      // URL changed, disconnect and reconnect
      console.warn('[Session Broadcast] Broadcasting connection URL changed, disconnecting');
      broadcastingConnection.disconnect();
    } else {
      // same URL, no need to reconnect
      return;
    }
  }

  broadcastingConnection = new SignalerClient(broadcastingApiUrl);
  broadcastingConnection.connect().then((connection) => {
    broadcastingConnectionId = connection.connectionId;
    // A fresh successful connect (including an `online`-triggered rebuild after give-up) means the
    // socket is healthy again — leave degraded mode and stop the slow-poll (AVS-4330). This is the
    // clear path that WsClientReconnected cannot provide after a give-up (it only fires from the
    // auto-reconnect loop, which has already stopped).
    stopSyncDegradedPoll();
    logger.info('[Session Broadcast] WebSocket connected', { connectionId: connection.connectionId });
  });

  // Keep the echo-suppression key fresh across reconnects. The server drops broadcasts
  // whose `emitter` matches the sender's connection id; after a silent reconnect the
  // SignalerClient mints a NEW connection id, so without this the stale id no longer
  // matches and the client receives — and re-applies — its own writes. Mirrors the media
  // RTCClient's WsClientReconnected handling (webrtc/index.ts).
  broadcastingConnection.on(EWebsocketClientEvents.WsClientReconnected, (data) => {
    const newConnectionId = (data as { connectionId?: string } | undefined)?.connectionId;
    if (newConnectionId) {
      broadcastingConnectionId = newConnectionId;
      logger.info('[Session Broadcast] Reconnected — refreshed broadcasting connection id', {
        connectionId: newConnectionId,
      });
    }
    // Leave degraded mode and read-repair: a reconnect means we may have missed broadcasts while
    // the socket was down (AVS-4330). Dirty-key protection keeps any unflushed local edit.
    stopSyncDegradedPoll();
    void repairPublicSession();
  });

  // Auto-reconnect exhausted its attempts: fall back to a slow-poll read-repair so persisted state
  // stays fresh even though real-time broadcasts are dead (AVS-4330).
  broadcastingConnection.on(EWebsocketClientEvents.WsClientGaveUp, () => {
    startSyncDegradedPoll();
  });

  logger.log('[Session Broadcast] Broadcasting connection initialized');

  userSession().markRequirementReady('broadcastingEstablished');

  broadcastingConnection.on(EStreamSessionEvents.SessionUpdated, (data) => {
    logger.log('[Session Broadcast] Session update received', data);

    if (!data || typeof data !== 'object') {
      return;
    }

    const aveSessionId = (data as { aveSessionId?: string }).aveSessionId;
    logger.info('[Session Broadcast] aveSessionId from broadcast update', aveSessionId);

    // Guard against a stale server echo overwriting an aveSessionId set locally
    // by the integrator (e.g. LCC) in connectionCallback. The echo may carry
    // aveSessionId:'' if share()'s updatePublicSessionNow() fired before the
    // integrator's write reached the server — AVS-4267.
    const sessionData = data as Partial<IPublicSession>;
    let safeData: Partial<IPublicSession> = sessionData;
    if (aveSessionId === '' && userSession().get('aveSessionId')) {
      const copy: Partial<IPublicSession> = { ...sessionData };
      delete copy.aveSessionId;
      safeData = copy;
    }

    // Skip flushing if already processing a broadcast to prevent infinite loops
    if (isApplyingBroadcastUpdate) {
      applyRemoteSession(safeData, { dirtyKeys: getDirtyKeys() });
      updatePublicSession.cancel();
      return;
    }

    // Discard stale pending local updates — the server broadcast is authoritative.
    // The original 3D→2D reversion bug is prevented by atomic aveSessionId
    // clearing in experience.ts + immediate sync in syncToServer.ts.
    isApplyingBroadcastUpdate = true;
    try {
      updatePublicSession.cancel();
      // Routed through the single remote-apply chokepoint: it drops stale/reordered
      // payloads by version and applies only the keys the server actually sent (AVS-4330).
      applyRemoteSession(safeData, { dirtyKeys: getDirtyKeys() });
      updatePublicSession.cancel();
    } finally {
      isApplyingBroadcastUpdate = false;
    }
  });
}

export async function initWebRtc(
  sessionId = userSession().get('id'),
  callback?: () => void
): Promise<RTCClient | null> {
  // only remote sessions can initialize webrtc and voip should be allowed
  if (!userSession().voipEnabled) {
    // voip not allowed
    return null;
  }

  // AVS-4286: a dealer tab that inherited this session from another browsing context
  // must not publish its own dealer VOIP stream. When 3DE standalone opens the DCC
  // configurator it passes the session via the `e3d-sid` URL param; that DCC tab is
  // cross-origin, so the two tabs cannot coordinate client-side — instead each tab
  // self-identifies from its own inherited-session record. Without this guard both tabs
  // publish and the customer sees the dealer twice. The originating standalone tab
  // (no inherited record) keeps the single dealer stream; customers are unaffected.
  //
  // AVS-4302: under the single-stream election this narrow suppression is superseded.
  // An inherited DCC tab must be allowed to CONNECT (as a receive-only standby) so the
  // customer-side dedup and manual takeover work — returning null here would deny it a
  // connection entirely. So the guard only applies when the election is OFF, in which
  // case behaviour is exactly as today (skew-safe, minimal regression).
  if (!userSession().isVoipSingleStreamEnabled) {
    if (user.type === 'dealer' && !!sessionId && isInheritedSession(sessionId)) {
      return null;
    }
  }

  // already connected, call callback immediately if provided
  if (voipRtcConnection?.sessionId === sessionId) {
    if (callback) {
      callback();
    }
    return voipRtcConnection;
  }

  // if presentation if completed but we try to init rtc, then something is wrong.
  // proceeding is not allowed and also make sure that the previous connection is properly destroyed
  if (userSession().presentationCompleted) {
    voipRtcConnection?.destroy();
    return null;
  }

  // AVS-4302: a non-master standby (another tab of the SAME dealer already owns the
  // voipMasters[myPseudonym] slot) must never grab the camera. Skipping the whole block means it
  // never calls `myCameraStream().init()` AND the receive-only toast below never fires
  // for an INTENTIONAL standby (GATE W6) — a standby is receive-only by design, not by
  // permission denial. `isVoipStandby` is false when the flag is off and false for a
  // different dealer / a vacant slot, so a flag-off tab and a vacant-slot dealer still
  // grab the camera exactly as today.
  // Try to initialize camera stream, but continue establishing RTC connection even if it fails
  // This allows receive-only mode for users who deny camera/mic permissions
  if (!userSession().isVoipStandby && !myCameraStream().stream) {
    const initPromise = new Promise<void>((resolve) => {
      let unsubscribeStarted: (() => void) | null = null;
      let unsubscribeError: (() => void) | null = null;

      const onStreamStarted = () => {
        cleanup();
        resolve();
      };

      const onStreamError = () => {
        cleanup();
        // Don't abort - continue in receive-only mode
        logger.log('[VOIP] Camera/mic denied - continuing in receive-only mode');

        // Show warning toast to inform user about receive-only mode
        showToast({
          title: t`Camera and microphone unavailable`,
          message: t`Your camera and/or microphone are not allowed or not available. You will only be able to see the other party's video stream. To enable your camera and microphone, update your browser permissions and reload the page.`,
          type: 'warn',
          notificationId: 'WARN_VOIP_RECEIVE_ONLY',
        });

        resolve();
      };

      const cleanup = () => {
        if (unsubscribeStarted) unsubscribeStarted();
        if (unsubscribeError) unsubscribeError();
      };

      unsubscribeStarted = myCameraStream().once('stream:started', onStreamStarted);
      unsubscribeError = myCameraStream().once('stream:error', onStreamError);

      myCameraStream().init();
    });

    // Wait for stream initialization attempt, then continue regardless of result
    await initPromise;
  }

  const iceServers = await getIceServers();
  if (!iceServers) {
    console.error(`No ICE servers found, RTC won't work`);
    return null;
  }
  // A dealer carries a stable per-dealer identity (userPseudonym) so peers can
  // recognise the same human across tabs (AVS-4302). A customer has no dealer
  // identity, so the param is only appended for dealers when a pseudonym exists.
  const dealerPseudonymParam =
    user.type === 'dealer' && user.userPseudonym ? `&dealerPseudonym=${encodeURIComponent(user.userPseudonym)}` : '';
  voipRtcConnection = RTCClient.getInstance(
    iceServers,
    `${process.env.SIGNALING_ORIGIN}?sessionId=${sessionId}&topicId=user-stream&userType=${user.type}${dealerPseudonymParam}`
  );
  voipRtcConnection.sessionId = sessionId;
  // AVS-4302: announce the (re)built connection so mounted React consumers re-subscribe to the
  // NEW store (a takeover/step-down rebuild happens under a still-mounted VoipPanel — see
  // voipConnectionEpoch). Bump AFTER the reassignment so consumers read the fresh binding.
  voipConnectionEpoch.setValues({ value: voipConnectionEpoch.get('value') + 1 });

  // AVS-4302: drop any handlers left over from a previous initWebRtc before
  // re-registering, so a repeated init→destroy→init cycle does not stack duplicate
  // voip:connected / peer:* / session:end listeners.
  cleanupVoipHandlers();

  if (callback) {
    voipHandlerUnsubscribes.push(voipRtcConnection.on('voip:connected', callback));
  }

  await voipRtcConnection.connect();

  voipHandlerUnsubscribes.push(
    voipRtcConnection.on(
      'peer:disconnected',
      (_connectionId: string, _remoteConnections: Record<string, TRTCConnection>, meta?: TRTCConnection['meta']) => {
        // AVS-4302: don't announce this dealer's own tab leaving (e.g. the old master stepping
        // down on a takeover) — that is "me", not a peer. The leaving peer's meta is captured
        // before removal (see ConnectionRemoved) since it is gone from remoteConnections here.
        if (isOwnDealerPeer({ meta }, user.userPseudonym, user.isDealer)) return;
        showToast({
          title: t`Your peer disconnected from the VOIP session`,
          type: 'warn',
          notificationId: 'WARN_VOIP_PEER_DISCONNECTED',
        });
      }
    )
  );

  voipHandlerUnsubscribes.push(
    voipRtcConnection.on(
      'peer:connected',
      (connectionId: string, remoteConnections: Record<string, TRTCConnection>) => {
        // AVS-4302: don't announce this dealer's own tab connecting (a second same-dealer tab,
        // or a takeover reconnect) — that is "me", not a peer. A customer or a different dealer
        // is still announced.
        if (isOwnDealerPeer(remoteConnections?.[connectionId], user.userPseudonym, user.isDealer)) return;
        showToast({
          title: t`Your peer connected to the VOIP session`,
          type: 'success',
          notificationId: 'SUCCESS_VOIP_PEER_CONNECTED',
        });
      }
    )
  );

  voipHandlerUnsubscribes.push(
    userSession().once('session:end', () => {
      cleanupVoipHandlers();
      voipRtcConnection?.destroy();
      broadcastingConnection?.disconnect();
    })
  );

  if (isDebugMode()) {
    // @ts-expect-error - expose config in global scope for debugging
    window.voipRtcConnection = voipRtcConnection;
  }

  return voipRtcConnection;
}
